import { gql, useApolloClient } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { useTranslation } from 'react-i18next';
import {
  EditStopMutationVariables,
  GetStopWithRouteGraphDataByIdDocument,
  GetStopWithRouteGraphDataByIdQuery,
  GetStopWithRouteGraphDataByIdQueryVariables,
  ReusableComponentsVehicleModeEnum,
  ScheduledStopPointAllFieldsFragment,
  ServicePatternScheduledStopPoint,
  ServicePatternScheduledStopPointSetInput,
  StopRegistryQuay,
  StopRegistryStopPlaceInput,
  useEditStopMutation,
  useEditStopPlaceMutation,
} from '../../../../generated/graphql';
import {
  DirectionNotResolvedError,
  EditRouteTerminalStopsError,
  IncompatibleDirectionsError,
  IncompatibleWithExistingRoutesError,
  InternalError,
  LinkNotResolvedError,
  StopPointUpdateFailed,
  TiamatUpdateFailedError,
  TimingPlaceRequiredError,
  defaultTo,
  illegalOptionalCast,
  showDangerToast,
  showDangerToastWithError,
} from '../../../../utils';
import { useGetConflictingStops } from '../../../LinesAndRoutes/Common';
import { wrapErrors } from '../../../StopRegistry/Stops/StopDetails/StopVersion/Utils/wrapErrors';
import { EditChanges, EditParams } from '../Types';
import { useGetRoutesBrokenByStopChange } from './useGetRoutesBrokenByStopChange';
import { useGetStopLinkAndDirection } from './useGetStopLinkAndDirection';
import { useValidateTimingSettings } from './useValidateTimingSettings';

const GQL_EDIT_STOP = gql`
  mutation EditStop(
    $stop_id: uuid!
    $stop_label: String!
    $stop_patch: service_pattern_scheduled_stop_point_set_input!
    $delete_from_journey_pattern_ids: [uuid!]!
  ) {
    # delete the stop from the journey patterns BEFORE editing the stop itself,
    # because Hasura checks deferred constraints after each root mutation
    delete_journey_pattern_scheduled_stop_point_in_journey_pattern(
      where: {
        _and: {
          scheduled_stop_point_label: { _eq: $stop_label }
          journey_pattern_id: { _in: $delete_from_journey_pattern_ids }
        }
      }
    ) {
      returning {
        journey_pattern_id
        scheduled_stop_point_label
        scheduled_stop_point_sequence
        is_used_as_timing_point
        is_regulated_timing_point
        is_loading_time_allowed
        is_via_point
        via_point_name_i18n
        via_point_short_name_i18n
        journey_pattern {
          journey_pattern_id
          on_route_id
        }
      }
    }

    # edit the stop itself
    update_service_pattern_scheduled_stop_point(
      where: { scheduled_stop_point_id: { _eq: $stop_id } }
      _set: $stop_patch
    ) {
      returning {
        priority
        direction
        scheduled_stop_point_id
        label
        timing_place_id
        timing_place {
          timing_place_id
          label
        }
        point_type
        validity_start
        validity_end
        located_on_infrastructure_link_id
        stop_place_ref
        measured_location
        relative_distance_from_infrastructure_link_start
        closest_point_on_infrastructure_link
        vehicle_mode_on_scheduled_stop_point {
          vehicle_mode
        }
      }
    }
  }
`;

const GQL_EDIT_STOP_PLACE = gql`
  mutation EditStopPlace($patch: stop_registry_StopPlaceInput) {
    stop_registry {
      mutateStopPlace(StopPlace: $patch) {
        id
        version

        name {
          value
        }

        geometry {
          coordinates
        }

        keyValues {
          key
          values
        }
      }
    }
  }
`;

function mapEditChangesToVariables(
  changes: EditChanges,
): EditStopMutationVariables {
  return {
    stop_id: changes.stopId,
    stop_label: changes.stopLabel,
    stop_patch: changes.stopPointPatch ?? {},
    delete_from_journey_pattern_ids:
      changes.deleteStopFromJourneyPatternIds ?? [],
  };
}

function quayPatchToStopPlacePatch(
  stopPlaceId: string | null | undefined,
  quayId: string | null | undefined,
  quayPatch: Pick<StopRegistryQuay, 'keyValues'> | null,
): StopRegistryStopPlaceInput | null {
  if (!stopPlaceId || !quayId) {
    return null;
  }

  const quayPatchWithId = { ...quayPatch, id: quayId };

  // If there are no actual changes -> null
  const changedFields = Object.entries(quayPatchWithId)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value)
    .map(([key]) => key);

  if (isEqual(changedFields, ['id'])) {
    return null;
  }

  return { id: stopPlaceId, quays: [quayPatchWithId] };
}

function hasStopPointFieldChanged(
  patch: ServicePatternScheduledStopPointSetInput,
  existing: ServicePatternScheduledStopPoint,
  field: 'priority' | 'validity_start' | 'validity_end',
) {
  if (field in patch) {
    return patch[field] !== existing[field];
  }

  return false;
}

function useGetConflictingStopsOnPrepare() {
  const getConflictingStops = useGetConflictingStops();

  return async (
    stopId: string,
    label: string,
    patch: ServicePatternScheduledStopPointSetInput,
    existing: ServicePatternScheduledStopPoint,
  ) => {
    const hasEditedValidity =
      hasStopPointFieldChanged(patch, existing, 'priority') ||
      hasStopPointFieldChanged(patch, existing, 'validity_start') ||
      hasStopPointFieldChanged(patch, existing, 'validity_end');

    if (!hasEditedValidity) {
      return [];
    }

    return getConflictingStops(
      {
        label,
        priority: patch.priority ?? existing.priority,
        validityStart:
          defaultTo(patch.validity_start, existing.validity_start) ?? undefined,
        validityEnd:
          defaultTo(patch.validity_end, existing.validity_end) ?? undefined,
      },
      stopId,
    );
  };
}

type OnStopLocationChangedResult = Pick<
  EditChanges,
  'stopPointPatch' | 'deleteStopFromJourneyPatternIds' | 'deleteStopFromRoutes'
>;

function useOnStopLocationChanged() {
  const getRoutesBrokenByStopChange = useGetRoutesBrokenByStopChange();
  const getStopLinkAndDirection = useGetStopLinkAndDirection();

  return async (
    oldStop: ScheduledStopPointAllFieldsFragment,
    newStop: ServicePatternScheduledStopPointSetInput,
    stopId: UUID,
  ): Promise<OnStopLocationChangedResult> => {
    // if we modified the location of the stop, have to also fetch the new infra link and direction
    const { closestLink, direction } = await getStopLinkAndDirection({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stopLocation: newStop.measured_location!,
      vehicleMode:
        oldStop.vehicle_mode_on_scheduled_stop_point?.[0]?.vehicle_mode ??
        ReusableComponentsVehicleModeEnum.Bus,
    });

    const {
      brokenJourneyPatternIds: deleteStopFromJourneyPatternIds,
      brokenRoutes: deleteStopFromRoutes,
    } = await getRoutesBrokenByStopChange({
      newLink: closestLink,
      newDirection: direction,
      newStop,
      label: newStop.label ?? oldStop.label,
      priority: newStop.priority ?? oldStop.priority,
      stopId,
      vehicleMode:
        oldStop.vehicle_mode_on_scheduled_stop_point?.[0]?.vehicle_mode ?? null,
    });

    return {
      stopPointPatch: {
        located_on_infrastructure_link_id: closestLink.infrastructure_link_id,
        direction,
      },
      deleteStopFromJourneyPatternIds,
      deleteStopFromRoutes,
    };
  };
}

function useGetLocationChanges() {
  const onStopLocationChanged = useOnStopLocationChanged();

  return async (
    stopId: string,
    patch: ServicePatternScheduledStopPointSetInput,
    stopWithRouteGraphData: ServicePatternScheduledStopPoint,
  ): Promise<Partial<OnStopLocationChangedResult>> => {
    const newLocation = patch.measured_location;
    const oldLocation = stopWithRouteGraphData.measured_location;

    const hasLocationChanged =
      newLocation && !isEqual(newLocation.coordinates, oldLocation.coordinates);

    if (!hasLocationChanged) {
      return {};
    }

    return onStopLocationChanged(stopWithRouteGraphData, patch, stopId);
  };
}

function useValidateTimingPlaceChanges() {
  const [validateTimingSettings] = useValidateTimingSettings();

  return async (
    stopLabel: string,
    patch: ServicePatternScheduledStopPointSetInput,
    stopWithRouteGraphData: ServicePatternScheduledStopPoint,
  ) => {
    if (patch.timing_place_id === undefined) {
      return;
    }

    const newTimingPlaceId = patch.timing_place_id;
    const oldTimingPlaceId = stopWithRouteGraphData.timing_place_id;

    const hasTimingPlaceIdChanged = !isEqual(
      newTimingPlaceId,
      oldTimingPlaceId,
    );

    if (hasTimingPlaceIdChanged) {
      await validateTimingSettings({
        stopLabel,
        timingPlaceId: newTimingPlaceId,
      });
    }
  };
}

// prepare variables for mutation and validate if it's even allowed
// try to produce a changeset that can be displayed on an explanatory UI
export function usePrepareEdit() {
  const apollo = useApolloClient();

  const getConflictingStops = useGetConflictingStopsOnPrepare();
  const getLocationChanges = useGetLocationChanges();
  const validateTimingPlaceChanges = useValidateTimingPlaceChanges();

  return async ({
    stopLabel,
    stopId,
    stopPointPatch,
    stopPlaceId,
    quayId,
    quayPatch,
  }: EditParams): Promise<EditChanges> => {
    const stopWithRoutesResult = await apollo.query<
      GetStopWithRouteGraphDataByIdQuery,
      GetStopWithRouteGraphDataByIdQueryVariables
    >({
      query: GetStopWithRouteGraphDataByIdDocument,
      variables: { stopId },
    });

    const stopWithRouteGraphData =
      illegalOptionalCast<ServicePatternScheduledStopPoint>(
        stopWithRoutesResult.data.service_pattern_scheduled_stop_point.at(0),
      );

    if (!stopWithRouteGraphData) {
      throw new InternalError(`Could not find stop with id ${stopId}`);
    }

    // changes that will always be applied
    const defaultChanges: Omit<
      EditChanges,
      'editedStop' | 'conflicts' | 'stopPlacePatch'
    > = {
      stopId,
      stopLabel,
      stopPointPatch,
      deleteStopFromRoutes: [],
      deleteStopFromJourneyPatternIds: [],
      quayId,
    };

    // Perform async tasks in parallel to speed things up.
    const [conflicts, locationChanges] = await Promise.all([
      getConflictingStops(
        stopId,
        stopLabel,
        stopPointPatch,
        stopWithRouteGraphData,
      ),

      // changes that are applied if the stop's location is changed
      getLocationChanges(stopId, stopPointPatch, stopWithRouteGraphData),

      // validate stop's timing settings in journey patterns if stop's timing place has been changed
      validateTimingPlaceChanges(
        stopLabel,
        stopPointPatch,
        stopWithRouteGraphData,
      ),
    ]);

    const mergedChanges = merge(
      {},
      defaultChanges,
      { conflicts },
      locationChanges,
    );

    return {
      ...mergedChanges,
      stopPointPatch: isEmpty(mergedChanges.stopPointPatch)
        ? null
        : mergedChanges.stopPointPatch,
      stopPlacePatch: quayPatchToStopPlacePatch(stopPlaceId, quayId, quayPatch),
      // the final state of the stop that will be after patching
      editedStop: merge(
        {},
        stopWithRouteGraphData,
        mergedChanges.stopPointPatch,
      ),
    };
  };
}

// default handler that can be used to show error messages as toast
// in case an exception is thrown
export function useDefaultErrorHandler() {
  const { t } = useTranslation();

  return (err: Error) => {
    if (err instanceof LinkNotResolvedError) {
      return showDangerToast(t(($) => $.stops.fetchClosestLinkFailed));
    }

    if (err instanceof DirectionNotResolvedError) {
      return showDangerToast(t(($) => $.stops.fetchDirectionFailed));
    }

    if (err instanceof IncompatibleDirectionsError) {
      return showDangerToast(t(($) => $.stops.incompatibleDirections));
    }

    if (err instanceof EditRouteTerminalStopsError) {
      return showDangerToast(t(($) => $.stops.cannotEditTerminalStops));
    }

    if (err instanceof IncompatibleWithExistingRoutesError) {
      return showDangerToast(
        t(($) => $.stops.stopBreaksRoutes, {
          routeLabels: err.message,
        }),
      );
    }

    if (err instanceof TimingPlaceRequiredError) {
      return showDangerToast(
        t(($) => $.stops.timingPlaceRequired, {
          routeLabels: err.message,
        }),
      );
    }

    if (err instanceof TiamatUpdateFailedError) {
      return showDangerToast(
        t(($) => $.stops.tiamatUpdateFailed, {
          errorMessage: err.causeMessage,
        }),
      );
    }

    if (err instanceof StopPointUpdateFailed) {
      return showDangerToast(
        t(($) => $.stops.stopPointUpdateFailed, {
          errorMessage: err.causeMessage,
        }),
      );
    }

    // if other error happened, show the generic error message with
    // whatever detail we can extract from it (e.g. GraphQL/network error).
    return showDangerToastWithError(
      t(($) => $.errors.saveFailed),
      err,
    );
  };
}

const refetchQueries = {
  awaitRefetchQueries: true,
  refetchQueries: () => ['GetStopPlaceDetails', 'GetStopInfoForEditingOnMap'],
} as const;

export function useEditStop() {
  const [editStopMutation] = useEditStopMutation();
  const [editStopPlaceMutation] = useEditStopPlaceMutation();

  return async (changes: EditChanges) => {
    let updateStopPointResult: Awaited<
      ReturnType<typeof editStopMutation>
    > | null = null;
    let updateStopPlaceResult: Awaited<
      ReturnType<typeof editStopPlaceMutation>
    > | null = null;

    const updateStopPoint =
      !!changes.stopPointPatch ||
      !!changes.deleteStopFromJourneyPatternIds?.length;

    if (changes.stopPlacePatch) {
      updateStopPlaceResult = await wrapErrors(
        editStopPlaceMutation({
          variables: { patch: changes.stopPlacePatch },

          // If we are not going to update the StopPoint, refetch any and all
          // active map queries right away. Else defer to after StopPoint update.
          ...(!updateStopPoint ? refetchQueries : {}),
        }),
        TiamatUpdateFailedError,
        'Failed to update Stop Place in Tiamat!',
      );
    }

    if (updateStopPoint) {
      const variables = mapEditChangesToVariables(changes);
      updateStopPointResult = await wrapErrors(
        editStopMutation({
          variables,

          // Always refetch map queries after StopPoint update.
          ...refetchQueries,
        }),
        StopPointUpdateFailed,
        'Failed to update Scheduled Stop Point in Lines & routes DB!',
      );
    }

    return { updateStopPointResult, updateStopPlaceResult };
  };
}
